import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail, API, getToken } from "../lib/api";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  Plus, Video, Sparkles, Image as ImageIcon, Mic, Captions, Film, Play,
  Trash2, Loader2, Download, RefreshCw, Check, AlertCircle, Settings as SettingsIcon,
  ChevronLeft, Pencil, Bot, Scissors, UserRound, Wallet, Share2, Link2,
} from "lucide-react";
import ChatHint from "../components/ChatHint";
import EmptyStateCards from "../components/EmptyStateCards";

const STATUS_META = {
  entwurf:         { label: "Entwurf",             color: "bg-slate-500/15 text-slate-300 border-slate-500/30" },
  skript:          { label: "Skript",              color: "bg-indigo-500/15 text-indigo-300 border-indigo-500/30" },
  storyboard:      { label: "Storyboard",          color: "bg-indigo-500/15 text-indigo-300 border-indigo-500/30" },
  wartet_freigabe: { label: "wartet auf Freigabe", color: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  rendering:       { label: "Rendering",           color: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  fertig:          { label: "fertig",              color: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  freigegeben:     { label: "freigegeben",         color: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  fehlgeschlagen:  { label: "fehlgeschlagen",      color: "bg-red-500/15 text-red-300 border-red-500/30" },
};

export default function VideoStudio() {
  const [projects, setProjects] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newOpen, setNewOpen] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [caps, setCaps] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [p, c] = await Promise.all([
        api.get("/video/projects"),
        api.get("/video/capabilities"),
      ]);
      setProjects(p.data);
      setCaps(c.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const removeProject = async () => {
    try {
      await api.delete(`/video/projects/${confirmDel.id}`);
      toast.success("Projekt gelöscht");
      setConfirmDel(null);
      if (selectedId === confirmDel.id) setSelectedId(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (selectedId) {
    return <ProjectEditor projectId={selectedId} onBack={() => { setSelectedId(null); load(); }} caps={caps} />;
  }

  return (
    <div className="p-6 lg:p-10 max-w-[1400px] mx-auto" data-testid="video-studio-page">
      <header className="flex items-start justify-between mb-6 gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-emerald-400">Studio</div>
          <h1 className="text-3xl font-extrabold tracking-tight mt-1 text-white">Video-Studio</h1>
          <p className="text-sm text-slate-400 mt-2">
            Produktvideos skripten, Storyboard rendern, Erzähl-Stimme generieren und zum fertigen Video zusammenbauen.
          </p>
          <div className="mt-2"><ChatHint text={'Sag dem KI-Agenten: „Erstelle ein Reel für mein neues Produkt."'} /></div>
        </div>
        <Button onClick={() => setNewOpen(true)} data-testid="new-project-button" className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold shadow-lg shadow-emerald-500/20">
          <Plus className="h-4 w-4 mr-2" /> Neues Video-Projekt
        </Button>
      </header>

      {caps && !caps.ffmpeg && (
        <Card className="bg-red-500/5 border-red-500/30 mb-4">
          <CardContent className="p-4 text-sm text-red-300" data-testid="ffmpeg-missing-warning">
            ⚠ Video-Engine ist derzeit nicht verfügbar — Montage-Rendern ist blockiert. Bitte den Betreiber benachrichtigen.
          </CardContent>
        </Card>
      )}

      {caps && caps.providers?.some((p) => !p.configured) && (
        <details className="group mb-6">
          <summary className="cursor-pointer text-xs font-medium text-slate-400 hover:text-slate-200 select-none inline-flex items-center gap-1" data-testid="video-providers-advanced-toggle">
            <span className="group-open:rotate-90 transition inline-block">▸</span> Technisch (Video-Anbieter-Status)
          </summary>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
            {caps.providers.map((p) => (
              <Card key={p.name} className="bg-[#111827] border-white/5" data-testid={`provider-${p.name}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-sm text-white font-medium">{p.label}</div>
                    {p.active && <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 border text-[10px]">aktiv</Badge>}
                  </div>
                  <div className="text-[11px] text-slate-500">
                    {p.configured ? <span className="text-emerald-400">bereit</span> : <span className="text-amber-400">noch nicht verbunden</span>}
                  </div>
                  {!p.configured && (
                    <div className="text-[11px] text-slate-500 mt-1">
                      Betreiber muss {p.env_keys.length} Zugangsdaten im Server hinterlegen.
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </details>
      )}

      <Card className="bg-[#111827] border-white/5">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 text-slate-400 text-center">Lade…</div>
          ) : projects.length === 0 ? (
            <EmptyStateCards
              testid="video-empty"
              title="Noch keine Video-Projekte"
              subtitle="Beschreibe ein Video — der Rest läuft Schritt für Schritt."
              cards={[
                { icon: Plus, title: "Video anlegen", body: "Titel + ein paar Zeilen Brief. Alles weitere im Studio.", onClick: () => setNewOpen(true), testid: "empty-card-create" },
                { icon: Bot, title: "KI-Agent fragen", body: 'Sag: „Erstelle ein Reel für mein neues Produkt" — fertig.', to: "/ki-agent", testid: "empty-card-chat" },
                { icon: Scissors, title: "Clip hochladen", body: "Lade eigene Clips im Bild-Studio hoch und mische sie ein.", to: "/bild-studio", testid: "empty-card-upload" },
              ]}
            />
          ) : (
            <div className="divide-y divide-white/5">
              {projects.map((p) => (
                <button key={p.id} onClick={() => setSelectedId(p.id)}
                  data-testid={`project-row-${p.id}`}
                  className="w-full text-left p-4 hover:bg-white/[0.02] transition flex items-center gap-4">
                  <div className="h-10 w-10 rounded bg-white/5 grid place-items-center text-emerald-400">
                    <Video className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-white font-medium truncate">{p.title}</div>
                    <div className="text-xs text-slate-500 truncate">
                      {p.format} · {p.language.toUpperCase()} · {p.render_mode === "avatar-video" ? "Avatar-Video" : p.render_mode === "ki-motion" ? "KI-Motion" : "Montage"} · {p.scenes?.length || 0} Szenen
                    </div>
                  </div>
                  <Badge className={`${STATUS_META[p.status]?.color || ""} border font-normal`} data-testid={`project-status-${p.id}`}>
                    {STATUS_META[p.status]?.label || p.status}
                  </Badge>
                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setConfirmDel(p); }}
                    data-testid={`project-delete-${p.id}`} className="text-red-400 hover:text-red-300">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <NewProjectDialog open={newOpen} onOpenChange={setNewOpen} onCreated={(id) => { setNewOpen(false); load(); setSelectedId(id); }} />

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent className="bg-[#111827] border-white/10 text-slate-100">
          <AlertDialogHeader>
            <AlertDialogTitle>Projekt löschen?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Das Projekt und alle Szenen werden entfernt. Final-Videos & Audios bleiben im Datei-Speicher.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="project-delete-cancel">Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={removeProject} data-testid="project-delete-confirm"
              className="bg-red-500 hover:bg-red-600">Löschen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function HeyGenAvatarPicker({ value, onChange }) {
  const [state, setState] = useState({ loading: true, error: "", avatars: [], voices: [] });
  const [preview, setPreview] = useState({ avatar: null });
  const audioRef = useRef(null);
  const [playingVoiceId, setPlayingVoiceId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [av, vo] = await Promise.all([
          api.get("/video/heygen/avatars", { params: { limit: 50 } }),
          api.get("/video/heygen/voices", { params: { language: "German", limit: 100 } }),
        ]);
        setState({ loading: false, error: "", avatars: av.data?.data || [], voices: vo.data?.data || [] });
      } catch (err) {
        setState({ loading: false, error: formatApiErrorDetail(err.response?.data?.detail) || "HeyGen nicht erreichbar", avatars: [], voices: [] });
      }
    })();
  }, []);

  const playVoice = (v) => {
    try { if (audioRef.current) { audioRef.current.pause(); audioRef.current.src = ""; } } catch {}
    if (!v?.preview_audio_url) { toast.info("Für diese Stimme ist keine Hörprobe hinterlegt."); return; }
    const a = new Audio(v.preview_audio_url);
    audioRef.current = a;
    setPlayingVoiceId(v.voice_id);
    a.addEventListener("ended", () => setPlayingVoiceId(null));
    a.addEventListener("error", () => { setPlayingVoiceId(null); toast.error("Hörprobe konnte nicht geladen werden."); });
    a.play().catch(() => setPlayingVoiceId(null));
  };

  if (state.loading) return (
    <div className="border-t border-white/5 pt-3" data-testid="avatar-picker">
      <div className="text-xs text-slate-400"><Loader2 className="h-3 w-3 inline animate-spin mr-1" />Lade HeyGen-Katalog…</div>
    </div>
  );
  if (state.error) return (
    <div className="border-t border-white/5 pt-3" data-testid="avatar-picker">
      <div className="rounded border border-amber-500/30 bg-amber-500/5 p-2 text-xs text-amber-200" data-testid="avatar-picker-error">{state.error}</div>
    </div>
  );
  return (
    <div className="border-t border-white/5 pt-3 space-y-4" data-testid="avatar-picker">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="text-xs text-slate-400">Avatar-Video nutzt HeyGen. Jede erzeugte Sekunde kostet HeyGen-Credits.</div>
        <div className="ml-auto"><HeyGenBalanceBadge compact /></div>
      </div>
      <div>
        <Label className="text-slate-300 text-xs mb-2 block">Avatar</Label>
        <div className="grid grid-cols-3 gap-2 max-h-80 overflow-y-auto pr-1" data-testid="heygen-avatar-grid">
          {state.avatars.map((a) => {
            const selected = value.avatar_id === a.id;
            const isPreviewing = preview.avatar === a.id && !!a.preview_video_url;
            return (
              <button type="button" key={a.id}
                onClick={() => {
                  if (!selected) onChange({ avatar_id: a.id });
                  else if (a.preview_video_url) setPreview({ avatar: isPreviewing ? null : a.id });
                }}
                className={`relative rounded-lg overflow-hidden border transition group ${selected ? "border-emerald-400 ring-2 ring-emerald-400/40" : "border-white/10 hover:border-white/30"}`}
                data-testid={`heygen-avatar-${a.id}`}>
                <div className="aspect-[3/4] bg-slate-800 relative">
                  {a.preview_image_url ? (
                    <img src={a.preview_image_url} alt={a.name || "Avatar"} loading="lazy"
                      className="h-full w-full object-cover" />
                  ) : (
                    <div className="h-full w-full grid place-items-center text-slate-500"><UserRound className="h-6 w-6" /></div>
                  )}
                  {isPreviewing && (
                    <video src={a.preview_video_url} autoPlay muted loop playsInline
                      className="absolute inset-0 h-full w-full object-cover" />
                  )}
                  {selected && (
                    <div className="absolute top-1.5 right-1.5 h-5 w-5 bg-emerald-500 rounded-full grid place-items-center text-white text-[11px] font-bold">✓</div>
                  )}
                  {a.preview_video_url && !selected && (
                    <div className="absolute bottom-1.5 right-1.5 text-[9px] bg-black/60 text-slate-200 rounded px-1 py-0.5">▶ Preview</div>
                  )}
                </div>
                <div className="p-1.5 text-left">
                  <div className="text-[11px] font-semibold text-white truncate">{a.name || a.id}</div>
                  {a.gender && <div className="text-[9px] text-slate-500 uppercase tracking-wider">{a.gender}</div>}
                </div>
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <Label className="text-slate-300 text-xs mb-2 block">Stimme (Deutsch)</Label>
        <div className="rounded-lg border border-white/10 max-h-56 overflow-y-auto divide-y divide-white/5" data-testid="heygen-voice-list">
          {state.voices.map((v) => {
            const selected = value.voice_id === v.voice_id;
            const playing = playingVoiceId === v.voice_id;
            return (
              <div key={v.voice_id}
                className={`flex items-center gap-2 p-2 cursor-pointer ${selected ? "bg-emerald-500/10" : "hover:bg-white/[0.03]"}`}
                onClick={() => onChange({ voice_id: v.voice_id })}
                data-testid={`heygen-voice-${v.voice_id}`}>
                <button type="button"
                  onClick={(e) => { e.stopPropagation(); playVoice(v); }}
                  className="h-7 w-7 rounded-full bg-white/10 hover:bg-emerald-500/30 grid place-items-center text-slate-200 shrink-0"
                  data-testid={`heygen-voice-play-${v.voice_id}`}
                  aria-label="Hörprobe">
                  {playing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <span className="text-[11px]">▶</span>}
                </button>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-medium text-white truncate">{v.name || v.voice_id}</div>
                  <div className="text-[10px] text-slate-500">
                    {[v.gender, v.language, v.language === "Multilingual" ? "mehrsprachig" : null].filter(Boolean).join(" · ")}
                  </div>
                </div>
                {selected && <div className="text-emerald-400 text-xs">✓</div>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function FinalVideoPlayer({ src, downloadHref, downloadName }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div className="rounded border border-amber-500/30 bg-amber-500/5 p-4 text-sm text-amber-100" data-testid="final-video-fallback">
        <div className="font-semibold mb-1">Video konnte in deinem Browser nicht abgespielt werden.</div>
        <div className="text-xs text-amber-200/80 mb-3">
          Das kann an Codec-Einstellungen deines Browsers liegen (Chrome, Edge, Safari und Firefox spielen H.264/AAC normalerweise ab).
          Die MP4-Datei ist aber unversehrt — du kannst sie jederzeit herunterladen.
        </div>
        <a href={downloadHref} download={downloadName}
          className="inline-flex items-center gap-1 text-xs text-emerald-300 hover:text-emerald-200"
          data-testid="final-video-fallback-download">
          <Download className="h-3.5 w-3.5" /> Video herunterladen
        </a>
      </div>
    );
  }
  return (
    <video src={src} controls
      onError={() => setFailed(true)}
      className="w-full max-h-[70vh] bg-black rounded border border-white/10"
      data-testid="final-video" />
  );
}

function HeyGenBalanceBadge({ compact = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = async (refresh = false) => {
    setLoading(true);
    try {
      const { data } = await api.get("/video/heygen/balance", { params: refresh ? { refresh: 1 } : {} });
      setData(data);
    } catch { setData({ configured: false }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  if (!data || !data.configured) return null;
  const amount = typeof data.remaining_balance === "number" ? data.remaining_balance.toFixed(2) : "—";
  const currencySym = data.currency === "usd" ? "$" : (data.currency || "").toUpperCase();
  const warn = data.low_balance;
  return (
    <div className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] ${warn ? "border-amber-500/40 bg-amber-500/10 text-amber-200" : "border-emerald-500/30 bg-emerald-500/5 text-emerald-200"}`}
      data-testid="heygen-balance-badge" title={data.auto_reload?.enabled ? `Auto-Aufladung: +${data.auto_reload.amount_usd}${currencySym} bei ${data.auto_reload.threshold_usd}${currencySym}` : "Keine Auto-Aufladung"}>
      <Wallet className="h-3 w-3" />
      <span>Guthaben: {amount}{currencySym}</span>
      {warn && <span className="font-semibold">· niedrig</span>}
      {!compact && (
        <button type="button" onClick={() => load(true)} disabled={loading}
          className="ml-1 text-slate-400 hover:text-white"
          data-testid="heygen-balance-refresh" aria-label="Guthaben aktualisieren">
          <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
        </button>
      )}
    </div>
  );
}

function RenderConfirmDialog({ open, onOpenChange, project, scenes, onConfirm }) {
  const [balance, setBalance] = useState(null);
  const mode = project?.render_mode || "montage";
  const sceneCount = scenes?.length || 0;
  const perScene = (scenes || []).some((s) => s.avatar_id || s.voice_id);
  const clipCount = mode === "avatar-video" ? (perScene ? sceneCount : 1) : 0;
  const estUsd = clipCount * 0.3; // same formula as the chat-agent approval card (tools.py)

  useEffect(() => {
    if (!open || mode !== "avatar-video") return;
    (async () => {
      try {
        const { data } = await api.get("/video/heygen/balance");
        setBalance(data);
      } catch { setBalance({ configured: false }); }
    })();
  }, [open, mode]);

  const modeLabel = mode === "avatar-video" ? "Avatar-Video" : mode === "ki-motion" ? "KI-Motion" : "Montage";
  const sym = balance?.currency === "usd" ? "$" : (balance?.currency || "").toUpperCase();
  const remaining = typeof balance?.remaining_balance === "number" ? balance.remaining_balance : null;
  const lowBalance = mode === "avatar-video" && remaining !== null && remaining < 1;
  const insufficient = mode === "avatar-video" && remaining !== null && remaining < estUsd;

  let costLine;
  if (mode === "avatar-video") {
    const costStr = estUsd.toFixed(2).replace(".", ",");
    costLine = perScene
      ? `${sceneCount} Szenen = ${sceneCount} HeyGen-Clips (ca. ${costStr} $)`
      : `1 Clip, ca. ${costStr} $`;
  } else if (mode === "ki-motion") {
    costLine = `${sceneCount} Szenen = ${sceneCount} KI-Motion-Clips (kostet Provider-Credits — exakter Betrag abhängig vom Anbieter)`;
  } else {
    costLine = `${sceneCount} Szenen · 0 $ zusätzlich (Montage nutzt bereits generierte Bilder/Stimme und ffmpeg)`;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#111827] border-white/10 text-slate-100 max-w-md" data-testid="render-confirm-dialog">
        <DialogHeader>
          <DialogTitle>Rendern starten?</DialogTitle>
          <DialogDescription className="text-slate-400">
            Prüfe Modus und Kosten, bevor der Render-Job startet.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="flex items-center justify-between rounded border border-white/10 bg-white/[0.03] px-3 py-2">
            <span className="text-slate-400">Modus</span>
            <span className="text-white font-medium" data-testid="render-confirm-mode">{modeLabel}</span>
          </div>
          <div className="flex items-center justify-between rounded border border-white/10 bg-white/[0.03] px-3 py-2">
            <span className="text-slate-400">Szenen</span>
            <span className="text-white font-medium" data-testid="render-confirm-scene-count">{sceneCount}</span>
          </div>
          <div className="rounded border border-white/10 bg-white/[0.03] px-3 py-2">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Geschätzte Kosten</div>
            <div className="text-white" data-testid="render-confirm-cost">{costLine}</div>
          </div>
          {mode === "avatar-video" && (
            <div className="flex items-center justify-between rounded border border-white/10 bg-white/[0.03] px-3 py-2">
              <span className="text-slate-400">HeyGen-Guthaben</span>
              <span className="text-white font-medium" data-testid="render-confirm-balance">
                {balance === null ? "…" : (balance?.configured && remaining !== null ? `${remaining.toFixed(2)}${sym}` : "nicht konfiguriert")}
              </span>
            </div>
          )}
          {lowBalance && (
            <div className="rounded border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-200" data-testid="render-confirm-low-balance">
              Niedriges Guthaben (unter 1 $). Je nach Szenenlänge könnte der Render abbrechen — bitte HeyGen-Guthaben aufladen.
            </div>
          )}
          {insufficient && (
            <div className="rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-200" data-testid="render-confirm-insufficient">
              Geschätzte Kosten liegen über dem aktuellen Guthaben. Render wird vermutlich fehlschlagen.
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}
            data-testid="render-confirm-cancel"
            className="text-slate-300 hover:text-white hover:bg-white/5">
            Abbrechen
          </Button>
          <Button onClick={onConfirm}
            data-testid="render-confirm-start"
            className="bg-emerald-500 hover:bg-emerald-600 text-white">
            Rendern starten
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}




function NewProjectDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({ title: "", brief: "", format: "9:16", language: "de", render_mode: "montage", voice: "nova", burn_in_subtitles: false, avatar_id: "", voice_id: "" });
  const [submitting, setSubmitting] = useState(false);
  const [heygen, setHeygen] = useState({ loading: false, configured: null, avatars: [], voices: [], error: "" });

  // Phase 20 — balance badge inside the dialog (reuses the server cache).
  const [balance, setBalance] = useState(null);
  useEffect(() => {
    if (form.render_mode !== "avatar-video") return;
    (async () => {
      try {
        const { data } = await api.get("/video/heygen/balance");
        setBalance(data);
      } catch { /* badge is non-blocking */ }
    })();
  }, [form.render_mode]);

  // Lazy-load HeyGen avatar + voice catalog when the owner picks avatar-video.
  useEffect(() => {
    if (form.render_mode !== "avatar-video") return;
    if (heygen.avatars.length || heygen.voices.length || heygen.loading) return;
    (async () => {
      setHeygen((h) => ({ ...h, loading: true, error: "" }));
      try {
        const [av, vo] = await Promise.all([
          api.get("/video/heygen/avatars", { params: { limit: 50 } }),
          api.get("/video/heygen/voices", { params: { language: "German", limit: 100 } }),
        ]);
        setHeygen({
          loading: false, configured: true, error: "",
          avatars: av.data?.data || [], voices: vo.data?.data || [],
        });
      } catch (err) {
        setHeygen({ loading: false, configured: false, avatars: [], voices: [],
          error: formatApiErrorDetail(err.response?.data?.detail) || "HeyGen nicht erreichbar" });
      }
    })();
  }, [form.render_mode]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.brief.trim()) return toast.error("Titel und Brief sind Pflicht");
    if (form.render_mode === "avatar-video" && (!form.avatar_id || !form.voice_id)) {
      return toast.error("Avatar-Video: Bitte Avatar und Stimme wählen.");
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (payload.render_mode !== "avatar-video") {
        delete payload.avatar_id;
        delete payload.voice_id;
      }
      const { data } = await api.post("/video/projects", payload);
      toast.success("Projekt angelegt");
      onCreated(data.id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSubmitting(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#111827] border-white/10 text-slate-100 max-w-xl max-h-[90vh] overflow-y-auto" data-testid="new-project-dialog">
        <DialogHeader>
          <DialogTitle>Neues Video-Projekt</DialogTitle>
          <DialogDescription className="text-slate-500">Brief + Format – Szenen werden danach vom KI-Agent generiert.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-slate-300 text-xs mb-1 block">Titel</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="z. B. Sommer-Teaser"
              data-testid="new-project-title" className="bg-white/5 border-white/10 text-slate-100" required />
          </div>
          <div>
            <Label className="text-slate-300 text-xs mb-1 block">Was soll im Video passieren?</Label>
            <Textarea rows={4} value={form.brief} onChange={(e) => setForm({ ...form, brief: e.target.value })}
              placeholder="Produktvideo für unsere Tagescreme — Zielgruppe Frauen 25-40, Hero-Produkt, warme Stimmung …"
              data-testid="new-project-brief" className="bg-white/5 border-white/10 text-slate-100" required />
          </div>
          <div>
            <Label className="text-slate-300 text-xs mb-1 block">Format</Label>
            <Select value={form.format} onValueChange={(v) => setForm({ ...form, format: v })}>
              <SelectTrigger data-testid="new-project-format" className="bg-white/5 border-white/10"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#111827] text-slate-100 border-white/10">
                <SelectItem value="9:16">Hochformat (TikTok / Reels)</SelectItem>
                <SelectItem value="1:1">Quadrat (Feed)</SelectItem>
                <SelectItem value="16:9">Querformat (YouTube)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <details className="group border-t border-white/5 pt-3">
            <summary className="cursor-pointer text-xs font-medium text-slate-400 hover:text-slate-200 select-none inline-flex items-center gap-1" data-testid="new-project-advanced-toggle">
              <span className="group-open:rotate-90 transition inline-block">▸</span> Erweitert (Sprache, Modus, Stimme)
            </summary>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <Label className="text-slate-300 text-xs mb-1 block">Sprache</Label>
                <Select value={form.language} onValueChange={(v) => setForm({ ...form, language: v })}>
                  <SelectTrigger data-testid="new-project-language" className="bg-white/5 border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#111827] text-slate-100 border-white/10">
                    <SelectItem value="de">Deutsch</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300 text-xs mb-1 block">Erzähl-Stimme</Label>
                <Select value={form.voice} onValueChange={(v) => setForm({ ...form, voice: v })} disabled={form.render_mode === "avatar-video"}>
                  <SelectTrigger data-testid="new-project-voice" className="bg-white/5 border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#111827] text-slate-100 border-white/10">
                    {["alloy", "echo", "fable", "onyx", "nova", "shimmer"].map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
                {form.render_mode === "avatar-video" && (
                  <div className="text-[10px] text-slate-500 mt-1">OpenAI-Stimme wird im Avatar-Video-Modus durch die HeyGen-Stimme ersetzt.</div>
                )}
              </div>
              <div className="col-span-2">
                <Label className="text-slate-300 text-xs mb-1 block">Render-Modus</Label>
                <Select value={form.render_mode} onValueChange={(v) => setForm({ ...form, render_mode: v })}>
                  <SelectTrigger data-testid="new-project-render-mode" className="bg-white/5 border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#111827] text-slate-100 border-white/10">
                    <SelectItem value="montage">Montage (empfohlen — Bilder + Erzähler)</SelectItem>
                    <SelectItem value="ki-motion">KI-Motion (bewegte Szenen)</SelectItem>
                    <SelectItem value="avatar-video">Avatar-Video (HeyGen — spricht dein Skript)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </details>

          {form.render_mode === "avatar-video" && (
            <HeyGenAvatarPicker
              value={{ avatar_id: form.avatar_id, voice_id: form.voice_id }}
              onChange={(v) => setForm({ ...form, ...v })}
            />
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} data-testid="new-project-cancel">Abbrechen</Button>
            <Button type="submit" disabled={submitting} data-testid="new-project-submit" className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold">
              {submitting ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Lege an…</> : <>Video-Projekt anlegen</>}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ProjectEditor({ projectId, onBack, caps }) {
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [heygenCatalog, setHeygenCatalog] = useState({ loaded: false, avatars: {}, voices: {} });
  const [renderConfirmOpen, setRenderConfirmOpen] = useState(false);

  const absUrl = (u) => (u?.startsWith("http") ? u : `${API.replace(/\/api$/, "")}${u}`);
  const token = getToken();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/video/projects/${projectId}`);
      setProject(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [projectId]);

  // Lazily fetch HeyGen catalog once per avatar-video project so scene rows can label
  // the active speaker ("Projekt-Sprecher" vs the overridden avatar/voice name).
  useEffect(() => {
    if (!project || project.render_mode !== "avatar-video" || heygenCatalog.loaded) return;
    (async () => {
      try {
        const [av, vo] = await Promise.all([
          api.get("/video/heygen/avatars", { params: { limit: 100 } }),
          api.get("/video/heygen/voices", { params: { language: "German", limit: 100 } }),
        ]);
        const amap = {}; (av.data?.data || []).forEach((a) => { amap[a.id] = a; });
        const vmap = {}; (vo.data?.data || []).forEach((v) => { vmap[v.voice_id] = v; });
        setHeygenCatalog({ loaded: true, avatars: amap, voices: vmap });
      } catch { setHeygenCatalog({ loaded: true, avatars: {}, voices: {} }); }
    })();
  }, [project?.render_mode, heygenCatalog.loaded]);

  // Poll while rendering
  useEffect(() => {
    if (!project || project.status !== "rendering") return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [project?.status]);

  const call = async (name, fn) => {
    setBusy(name);
    try { await fn(); toast.success(`${name}: ok`); } 
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fehler"); }
    finally { setBusy(null); load(); }
  };

  if (loading || !project) return <div className="p-10 text-slate-400">Lade…</div>;

  const scenes = project.scenes || [];
  const allHaveImages = scenes.length > 0 && scenes.every((s) => s.image_id || s.clip_id);

  return (
    <div className="p-6 lg:p-10 max-w-[1400px] mx-auto" data-testid="project-editor">
      <Button variant="ghost" onClick={onBack} data-testid="editor-back"
        className="mb-4 text-slate-300 hover:text-white hover:bg-white/5">
        <ChevronLeft className="h-4 w-4 mr-1" /> Zurück
      </Button>

      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-white">{project.title}</h1>
          <div className="text-sm text-slate-400 mt-1">
            {project.format} · {project.language.toUpperCase()} · {project.render_mode === "avatar-video" ? "Avatar-Video" : project.render_mode === "ki-motion" ? "KI-Motion" : "Montage"}
          </div>
        </div>
        <Badge className={`${STATUS_META[project.status]?.color || ""} border font-normal`} data-testid="editor-status">
          {STATUS_META[project.status]?.label || project.status}
        </Badge>
      </div>

      {project.error && (
        <Card className="bg-red-500/5 border-red-500/30 mb-4" data-testid="project-error">
          <CardContent className="p-4 text-sm text-red-300">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5" />
              <div><strong>Fehler:</strong> {project.error}</div>
            </div>
          </CardContent>
        </Card>
      )}

      {project.tts_note && (
        <Card className="bg-amber-500/5 border-amber-500/30 mb-4" data-testid="project-tts-note">
          <CardContent className="p-4 text-sm text-amber-200">
            Hinweis zur Vertonung: {project.tts_note}
          </CardContent>
        </Card>
      )}

      {/* Stepper */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-6" data-testid="stepper">
        <StepBtn icon={Sparkles} label="1. Skript" onClick={() => call("Skript", () => api.post(`/video/projects/${projectId}/script`))}
          disabled={busy !== null} active={busy === "Skript"} done={scenes.length > 0} testid="step-script" />
        <StepBtn icon={ImageIcon} label="2. Storyboard" onClick={() => call("Storyboard", () => api.post(`/video/projects/${projectId}/storyboard`))}
          disabled={busy !== null || scenes.length === 0} active={busy === "Storyboard"} done={allHaveImages} testid="step-storyboard" />
        <StepBtn icon={Mic} label="3. Voiceover" onClick={() => call("Voiceover", () => api.post(`/video/projects/${projectId}/voiceover`))}
          disabled={busy !== null || scenes.length === 0} active={busy === "Voiceover"} done={scenes.some((s) => s.audio_id)} testid="step-voiceover" />
        <StepBtn icon={Captions} label="4. Untertitel" onClick={() => call("Untertitel", () => api.post(`/video/projects/${projectId}/subtitles`))}
          disabled={busy !== null || scenes.length === 0} active={busy === "Untertitel"} done={!!project.subtitles_srt_id} testid="step-subtitles" />
        <StepBtn icon={Film} label="5. Rendern" onClick={() => setRenderConfirmOpen(true)}
          disabled={busy !== null || (project.render_mode !== "avatar-video" && !allHaveImages) || project.status === "rendering"} active={project.status === "rendering"}
          done={project.status === "fertig" || project.status === "freigegeben"} testid="step-render" />
      </div>

      <RenderConfirmDialog open={renderConfirmOpen} onOpenChange={setRenderConfirmOpen}
        project={project} scenes={scenes}
        onConfirm={async () => {
          setRenderConfirmOpen(false);
          await call("Rendern", () => api.post(`/video/projects/${projectId}/render`));
        }} />

      {/* Scenes */}
      <Card className="bg-[#111827] border-white/5 mb-6">
        <CardContent className="p-5">
          <h2 className="text-sm font-semibold text-white uppercase tracking-wider mb-3">Szenen ({scenes.length})</h2>
          {scenes.length === 0 ? (
            <div className="text-sm text-slate-500 border border-dashed border-white/10 rounded p-6 text-center">
              Noch keine Szenen — klicke „1. Skript" oben, damit die KI aus deinem Brief Szenen erstellt.
            </div>
          ) : (
            <div className="space-y-3">
              {scenes.map((s, i) => (
                <SceneRow key={s.id} scene={s} index={i} projectId={projectId} absUrl={absUrl} onChange={load} token={token}
                  format={project.format} renderMode={project.render_mode} heygenCatalog={heygenCatalog}
                  projectAvatarId={project.avatar_id} projectVoiceId={project.voice_id} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Final preview */}
      {project.final_video_id && (
        <Card className="bg-[#111827] border-white/5" data-testid="final-video-card">
          <CardContent className="p-5">
            <h2 className="text-sm font-semibold text-white uppercase tracking-wider mb-3">Finales Video</h2>
            <FinalVideoPlayer
              src={absUrl(`/api/files/${project.final_video_id}?token=${token}`)}
              downloadHref={absUrl(`/api/files/${project.final_video_id}?token=${token}`)}
              downloadName={`${project.title}.mp4`}
            />
            <div className="flex gap-2 mt-3">
              <a href={absUrl(`/api/files/${project.final_video_id}?token=${token}`)} download={`${project.title}.mp4`}
                data-testid="final-video-download"
                className="inline-flex items-center text-xs text-emerald-400 hover:text-emerald-300">
                <Download className="h-3.5 w-3.5 mr-1" /> MP4 herunterladen
              </a>
              {project.subtitles_srt_id && (
                <a href={absUrl(`/api/files/${project.subtitles_srt_id}?token=${token}`)} download={`${project.title}.srt`}
                  data-testid="final-srt-download"
                  className="inline-flex items-center text-xs text-emerald-400 hover:text-emerald-300">
                  <Download className="h-3.5 w-3.5 mr-1" /> SRT
                </a>
              )}
              {project.subtitles_vtt_id && (
                <a href={absUrl(`/api/files/${project.subtitles_vtt_id}?token=${token}`)} download={`${project.title}.vtt`}
                  data-testid="final-vtt-download"
                  className="inline-flex items-center text-xs text-emerald-400 hover:text-emerald-300">
                  <Download className="h-3.5 w-3.5 mr-1" /> VTT
                </a>
              )}
              {project.status === "fertig" && (
                <Button size="sm" onClick={() => call("Freigeben", () => api.post(`/video/projects/${projectId}/approve`))}
                  data-testid="final-approve" className="ml-auto bg-emerald-500 hover:bg-emerald-600">
                  <Check className="h-3.5 w-3.5 mr-1" /> Freigeben
                </Button>
              )}
            </div>
            {(project.status === "fertig" || project.status === "freigegeben") && (
              <VideoShareAndHandoff projectId={projectId} />
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function VideoShareAndHandoff({ projectId }) {
  const [share, setShare] = useState({ active: false, token: null });
  const [busy, setBusy] = useState("");
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/video/projects/${projectId}/share`);
        setShare(data);
      } catch { /* non-blocking */ }
    })();
  }, [projectId]);
  const toSocial = async () => {
    setBusy("social");
    try {
      const { data } = await api.post(`/video/projects/${projectId}/to-social`);
      toast.success(data.captions_generated
        ? "Beitrag-Entwurf angelegt — 3 Captions generiert. Öffne Social Posts zur Freigabe."
        : "Beitrag-Entwurf angelegt — Captions können im Composer generiert werden.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(""); }
  };
  const createShare = async () => {
    setBusy("share");
    try {
      const { data } = await api.post(`/video/projects/${projectId}/share`, {});
      setShare({ active: true, token: data.token, created_at: data.created_at });
      const url = `${window.location.origin}/v/${data.token}`;
      try { await navigator.clipboard.writeText(url); toast.success("Share-Link kopiert"); }
      catch { toast.success("Share-Link erstellt"); }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(""); }
  };
  const revokeShare = async () => {
    setBusy("revoke");
    try {
      await api.delete(`/video/projects/${projectId}/share`);
      setShare({ active: false, token: null });
      toast.success("Share-Link widerrufen");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const copyLink = async () => {
    const url = `${window.location.origin}/v/${share.token}`;
    try { await navigator.clipboard.writeText(url); toast.success("Link kopiert"); }
    catch { toast.info(url); }
  };
  return (
    <div className="mt-4 pt-4 border-t border-white/5 flex flex-wrap gap-2 items-center" data-testid="video-handoff-row">
      <Button size="sm" variant="outline" onClick={toSocial} disabled={busy === "social"}
        data-testid="video-to-social" className="border-white/10 hover:bg-white/5">
        {busy === "social" ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Share2 className="h-3.5 w-3.5 mr-1" />}
        Als Beitrag übernehmen
      </Button>
      {share.active ? (
        <>
          <Button size="sm" variant="outline" onClick={copyLink}
            data-testid="video-share-copy" className="border-emerald-500/30 bg-emerald-500/5 text-emerald-200 hover:bg-emerald-500/15">
            <Link2 className="h-3.5 w-3.5 mr-1" /> Share-Link kopieren
          </Button>
          <span className="text-[11px] text-emerald-400" data-testid="video-share-active">· aktiv</span>
          <Button size="sm" variant="ghost" onClick={revokeShare} disabled={busy === "revoke"}
            data-testid="video-share-revoke" className="text-slate-400 hover:text-amber-400">
            {busy === "revoke" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Widerrufen"}
          </Button>
        </>
      ) : (
        <Button size="sm" variant="outline" onClick={createShare} disabled={busy === "share"}
          data-testid="video-share-create" className="border-white/10 hover:bg-white/5">
          {busy === "share" ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Link2 className="h-3.5 w-3.5 mr-1" />}
          Teilen (öffentlicher Link)
        </Button>
      )}
    </div>
  );
}

function StepBtn({ icon: Icon, label, onClick, disabled, active, done, testid }) {
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testid}
      className={`relative text-xs font-medium px-3 py-3 rounded-lg border transition flex flex-col items-center gap-1 ${
        active ? "bg-amber-500/15 border-amber-500/40 text-amber-200"
        : done ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-200"
        : disabled ? "bg-white/[0.02] border-white/5 text-slate-600 cursor-not-allowed"
        : "bg-white/[0.03] border-white/10 text-slate-300 hover:bg-white/5 hover:text-white"
      }`}>
      {active ? <Loader2 className="h-5 w-5 animate-spin" /> : <Icon className="h-5 w-5" />}
      {label}
      {done && <Check className="h-3 w-3 absolute top-1.5 right-1.5 text-emerald-300" />}
    </button>
  );
}

function ClipPickerDialog({ open, onOpenChange, onPick, absUrl, token }) {
  const [clips, setClips] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/images/gallery?kind=video");
        setClips(data);
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fehler beim Laden");
      } finally { setLoading(false); }
    })();
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#111827] border-white/10 text-slate-100 max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="clip-picker-dialog">
        <DialogHeader>
          <DialogTitle>Video-Clip anhängen</DialogTitle>
          <DialogDescription className="text-slate-500">
            Wähle einen bereits hochgeladenen Clip aus diesem Projekt. Clips lädst du im Bild-Studio bzw. als Upload hoch.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="p-6 text-center text-slate-400 text-sm">Lade Clips…</div>
        ) : clips.length === 0 ? (
          <div className="p-6 text-center text-slate-400 text-sm border border-dashed border-white/10 rounded" data-testid="clip-picker-empty">
            Noch keine Video-Clips in diesem Projekt. Lade zuerst eine .mp4/.mov/.webm-Datei hoch.
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {clips.map((c) => (
              <button key={c.id} onClick={() => onPick(c.id)}
                data-testid={`clip-option-${c.id}`}
                className="group text-left rounded border border-white/10 bg-black overflow-hidden hover:border-emerald-400/60 transition">
                <div className="aspect-video">
                  <video src={absUrl(`/api/files/${c.id}?token=${token}`)}
                    muted playsInline preload="metadata" className="w-full h-full object-cover" />
                </div>
                <div className="p-2">
                  <div className="text-xs text-slate-200 truncate">{c.original_name || c.id}</div>
                  <div className="text-[10px] text-slate-500">
                    {(c.duration_sec ? `${c.duration_sec.toFixed(1)}s · ` : "") + (c.width ? `${c.width}×${c.height}` : "")}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="clip-picker-cancel">Schließen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SceneRow({ scene, index, projectId, absUrl, onChange, token, format, renderMode, heygenCatalog, projectAvatarId, projectVoiceId }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(scene);
  const [busy, setBusy] = useState(false);
  const [clipPickerOpen, setClipPickerOpen] = useState(false);
  const [speakerOpen, setSpeakerOpen] = useState(false);

  const detachClip = async () => {
    try {
      await api.post(`/video/projects/${projectId}/scenes/${scene.id}/clip`, { clip_id: null });
      toast.success("Clip entfernt");
      onChange();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const attachClip = async (clipId) => {
    try {
      await api.post(`/video/projects/${projectId}/scenes/${scene.id}/clip`, { clip_id: clipId });
      toast.success("Clip angehängt");
      setClipPickerOpen(false);
      onChange();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const save = async () => {
    try {
      await api.put(`/video/projects/${projectId}/scenes/${scene.id}`, {
        narration: draft.narration, visual_prompt: draft.visual_prompt,
        duration_sec: Number(draft.duration_sec) || 4, text_overlay: draft.text_overlay,
      });
      toast.success("Szene aktualisiert");
      setEditing(false);
      onChange();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const regenImage = async () => {
    setBusy(true);
    try {
      await api.post(`/video/projects/${projectId}/scenes/${scene.id}/image`);
      toast.success("Bild neu generiert");
      onChange();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const del = async () => {
    try {
      await api.delete(`/video/projects/${projectId}/scenes/${scene.id}`);
      toast.success("Szene gelöscht");
      onChange();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="flex gap-3 p-3 rounded border border-white/5 bg-white/[0.02]" data-testid={`scene-${index}`}>
      <div className="shrink-0 w-32">
        <div className={`rounded border border-white/10 bg-black overflow-hidden flex items-center justify-center relative ${
          format === "9:16" ? "aspect-[9/16]" : format === "16:9" ? "aspect-video" : "aspect-square"
        }`}>
          {scene.clip_id ? (
            <video src={absUrl(`/api/files/${scene.clip_id}?token=${token}`)}
              muted playsInline className="w-full h-full object-cover"
              data-testid={`scene-clip-${index}`} />
          ) : scene.image_id ? (
            <img src={absUrl(`/api/files/${scene.image_id}?token=${token}`)} alt="" className="w-full h-full object-cover" data-testid={`scene-image-${index}`} />
          ) : (
            <ImageIcon className="h-6 w-6 text-slate-700" />
          )}
          {scene.clip_id && (
            <Badge data-testid={`scene-clip-badge-${index}`}
              className="absolute top-1 left-1 bg-emerald-500/80 text-white border-emerald-400/40 border text-[9px] px-1.5 py-0">
              Clip
            </Badge>
          )}
        </div>
        <Button size="sm" variant="outline" onClick={regenImage} disabled={busy || !!scene.clip_id}
          data-testid={`scene-regen-${index}`}
          title={scene.clip_id ? "Clip entfernen, um ein Bild zu generieren" : ""}
          className="w-full mt-2 h-7 text-xs border-white/10 bg-transparent text-slate-300 hover:bg-white/5 disabled:opacity-40">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <><RefreshCw className="h-3 w-3 mr-1" />Neu</>}
        </Button>
        {scene.clip_id ? (
          <Button size="sm" variant="outline" onClick={detachClip}
            data-testid={`scene-clip-detach-${index}`}
            className="w-full mt-1 h-7 text-xs border-red-500/30 bg-transparent text-red-300 hover:bg-red-500/10">
            <Trash2 className="h-3 w-3 mr-1" />Clip
          </Button>
        ) : (
          <Button size="sm" variant="outline" onClick={() => setClipPickerOpen(true)}
            data-testid={`scene-clip-attach-${index}`}
            className="w-full mt-1 h-7 text-xs border-white/10 bg-transparent text-slate-300 hover:bg-white/5">
            <Film className="h-3 w-3 mr-1" />Clip
          </Button>
        )}
      </div>
      <ClipPickerDialog open={clipPickerOpen} onOpenChange={setClipPickerOpen}
        onPick={attachClip} absUrl={absUrl} token={token} />
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-2">
          <Badge className="bg-white/5 text-slate-300 border-white/10 border text-[10px]">Szene {index + 1}</Badge>
          <span className="text-[11px] text-slate-500">{(scene.duration_sec || 0).toFixed(1)}s</span>
          {scene.audio_id && <Badge className="bg-emerald-500/10 text-emerald-300 border-emerald-500/30 border text-[10px]">Audio ✓</Badge>}
          <div className="ml-auto flex gap-1">
            <Button size="sm" variant="ghost" onClick={() => { setDraft(scene); setEditing(!editing); }}
              data-testid={`scene-edit-${index}`} className="h-7 text-slate-400 hover:text-white">
              <Pencil className="h-3 w-3" />
            </Button>
            <Button size="sm" variant="ghost" onClick={del}
              data-testid={`scene-delete-${index}`} className="h-7 text-red-400 hover:text-red-300">
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        </div>
        {editing ? (
          <div className="space-y-2">
            <Textarea rows={2} value={draft.narration} onChange={(e) => setDraft({ ...draft, narration: e.target.value })}
              data-testid={`scene-narration-input-${index}`} placeholder="Narration"
              className="text-xs bg-white/5 border-white/10 text-slate-100" />
            <Textarea rows={2} value={draft.visual_prompt} onChange={(e) => setDraft({ ...draft, visual_prompt: e.target.value })}
              data-testid={`scene-prompt-input-${index}`} placeholder="Visual-Prompt (EN)"
              className="text-xs bg-white/5 border-white/10 text-slate-100" />
            <div className="grid grid-cols-2 gap-2">
              <Input value={draft.text_overlay} onChange={(e) => setDraft({ ...draft, text_overlay: e.target.value })}
                data-testid={`scene-overlay-input-${index}`} placeholder="Overlay-Text (optional)"
                className="text-xs bg-white/5 border-white/10 text-slate-100" />
              <Input type="number" step="0.5" min="1" max="12" value={draft.duration_sec}
                onChange={(e) => setDraft({ ...draft, duration_sec: Number(e.target.value) })}
                data-testid={`scene-duration-input-${index}`} placeholder="Dauer (s)"
                className="text-xs bg-white/5 border-white/10 text-slate-100" />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={save} data-testid={`scene-save-${index}`} className="h-7 bg-emerald-500 hover:bg-emerald-600 text-xs">Speichern</Button>
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)} className="h-7 text-xs">Abbrechen</Button>
            </div>
          </div>
        ) : (
          <>
            <div className="text-sm text-slate-200 mb-1" data-testid={`scene-narration-${index}`}>{scene.narration || <span className="text-slate-500 italic">keine Narration</span>}</div>
            <div className="text-[11px] text-slate-500 font-mono truncate" data-testid={`scene-prompt-${index}`}>{scene.visual_prompt}</div>
            {scene.text_overlay && <div className="text-[11px] text-emerald-400 mt-1">Overlay: „{scene.text_overlay}"</div>}
            {scene.audio_id && (
              <audio controls src={absUrl(`/api/files/${scene.audio_id}?token=${token}`)}
                data-testid={`scene-audio-${index}`} className="mt-2 w-full h-8" />
            )}
            {renderMode === "avatar-video" && (
              <SceneSpeakerRow scene={scene} index={index} heygenCatalog={heygenCatalog}
                projectAvatarId={projectAvatarId} projectVoiceId={projectVoiceId}
                onOpen={() => setSpeakerOpen(true)} />
            )}
          </>
        )}
      </div>
      {renderMode === "avatar-video" && (
        <SceneSpeakerDialog open={speakerOpen} onOpenChange={setSpeakerOpen}
          scene={scene} index={index} projectId={projectId} onSaved={onChange} />
      )}
    </div>
  );
}

function SceneSpeakerRow({ scene, index, heygenCatalog, projectAvatarId, projectVoiceId, onOpen }) {
  const overridden = !!(scene.avatar_id || scene.voice_id);
  const effectiveAvatarId = scene.avatar_id || projectAvatarId || "";
  const effectiveVoiceId = scene.voice_id || projectVoiceId || "";
  const avatar = effectiveAvatarId ? heygenCatalog.avatars?.[effectiveAvatarId] : null;
  const voice = effectiveVoiceId ? heygenCatalog.voices?.[effectiveVoiceId] : null;
  const avatarLabel = avatar?.name || (effectiveAvatarId ? effectiveAvatarId.slice(0, 10) + "…" : "—");
  const voiceLabel = voice?.name || (effectiveVoiceId ? effectiveVoiceId.slice(0, 10) + "…" : "—");
  return (
    <div className="mt-2 flex items-center gap-2 flex-wrap text-[11px]" data-testid={`scene-speaker-row-${index}`}>
      {overridden && avatar?.preview_image_url ? (
        <img src={avatar.preview_image_url} alt=""
          data-testid={`scene-speaker-thumb-${index}`}
          className="h-7 w-7 rounded-full object-cover border border-white/10" />
      ) : (
        <div className="h-7 w-7 rounded-full bg-white/5 border border-white/10 grid place-items-center text-slate-500">
          <UserRound className="h-3.5 w-3.5" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        {overridden ? (
          <>
            <div className="text-slate-200 truncate" data-testid={`scene-speaker-label-${index}`}>
              {avatarLabel} · {voiceLabel}
            </div>
            <div className="text-[10px] text-slate-500">Eigener Sprecher für diese Szene</div>
          </>
        ) : (
          <>
            <div className="text-slate-400 truncate" data-testid={`scene-speaker-label-${index}`}>
              Projekt-Sprecher{avatar?.name ? ` · ${avatar.name}` : ""}
            </div>
            <div className="text-[10px] text-slate-500">Verwendet die Projekt-Einstellung</div>
          </>
        )}
      </div>
      <Button size="sm" variant="ghost" onClick={onOpen}
        data-testid={`scene-speaker-change-${index}`}
        className="h-7 px-2 text-xs text-slate-300 hover:text-white hover:bg-white/5 border border-white/10">
        Sprecher wechseln
      </Button>
    </div>
  );
}

function SceneSpeakerDialog({ open, onOpenChange, scene, index, projectId, onSaved }) {
  const [value, setValue] = useState({ avatar_id: scene.avatar_id || "", voice_id: scene.voice_id || "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setValue({ avatar_id: scene.avatar_id || "", voice_id: scene.voice_id || "" });
  }, [open, scene.avatar_id, scene.voice_id]);
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/video/projects/${projectId}/scenes/${scene.id}`, {
        avatar_id: value.avatar_id || "", voice_id: value.voice_id || "",
      });
      toast.success("Sprecher für Szene gespeichert");
      onSaved();
      onOpenChange(false);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fehler"); }
    finally { setSaving(false); }
  };
  const clear = async () => {
    setSaving(true);
    try {
      await api.put(`/video/projects/${projectId}/scenes/${scene.id}`, { avatar_id: "", voice_id: "" });
      toast.success("Projekt-Sprecher wird wieder verwendet");
      onSaved();
      onOpenChange(false);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fehler"); }
    finally { setSaving(false); }
  };
  const canSave = !!(value.avatar_id && value.voice_id);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#111827] border-white/10 text-slate-100 max-w-2xl" data-testid={`scene-speaker-dialog-${index}`}>
        <DialogHeader>
          <DialogTitle>Sprecher für Szene {index + 1}</DialogTitle>
          <DialogDescription className="text-slate-400">
            Wähle einen eigenen Avatar und eine Stimme für diese Szene. Wenn nichts gewählt ist, nutzt die Szene den Projekt-Sprecher.
          </DialogDescription>
        </DialogHeader>
        <HeyGenAvatarPicker value={value} onChange={(patch) => setValue({ ...value, ...patch })} />
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={clear} disabled={saving}
            data-testid={`scene-speaker-clear-${index}`}
            className="text-slate-300 hover:text-white hover:bg-white/5">
            Projekt-Sprecher nutzen
          </Button>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}
            className="text-slate-300 hover:text-white hover:bg-white/5">
            Abbrechen
          </Button>
          <Button onClick={save} disabled={!canSave || saving}
            data-testid={`scene-speaker-save-${index}`}
            className="bg-emerald-500 hover:bg-emerald-600 text-white">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Speichern"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
